import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus, Clock, Thermometer, Utensils, Wrench } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';
import { mockRefillStations } from './mock-data';

const columns = [
  {
    accessorKey: 'name',
    header: 'Station Name',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.location}</div>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.status)}>
        {row.original.status}
      </Badge>
    )
  },
  {
    accessorKey: 'capacity',
    header: 'Capacity',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">
          {row.original.capacity.inUse} / {row.original.capacity.total} in use
        </div>
        <div className="text-sm text-muted-foreground">
          {Math.round((row.original.capacity.inUse / row.original.capacity.total) * 100)}% utilized
        </div>
      </div>
    )
  },
  {
    accessorKey: 'products',
    header: 'Available Products',
    cell: ({ row }) => (
      <div className="space-y-1">
        {row.original.products.map((product) => (
          <Badge
            key={product}
            variant="outline"
            className={product.includes('Meatball') ? 'bg-[#FFDB00] border-[#FFDB00] text-[#0058AB]' : ''}
          >
            {product}
          </Badge>
        ))}
      </div>
    )
  },
  {
    accessorKey: 'operatingHours',
    header: 'Hours',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
        {row.original.operatingHours}
      </div>
    )
  },
  {
    accessorKey: 'maintenance',
    header: 'Maintenance',
    cell: ({ row }) => (
      <div>
        <div className="text-sm">Last: {new Date(row.original.lastMaintenance).toLocaleDateString()}</div>
        <div className="text-sm">Next: {new Date(row.original.nextMaintenance).toLocaleDateString()}</div>
      </div>
    )
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Details</DropdownMenuItem>
          <DropdownMenuItem>Schedule Maintenance</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
];

function getStatusVariant(status) {
  switch (status?.toLowerCase()) {
    case 'operational':
      return 'default';
    case 'maintenance':
      return 'destructive';
    case 'offline':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function RefillStationsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    location: 'all'
  });

  const stations = React.useMemo(() => {
    let filteredStations = [...mockRefillStations];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredStations = filteredStations.filter(station =>
        station.name.toLowerCase().includes(searchLower) ||
        station.location.toLowerCase().includes(searchLower) ||
        station.products.some(p => p.toLowerCase().includes(searchLower))
      );
    }

    if (filters.status !== 'all') {
      filteredStations = filteredStations.filter(station =>
        station.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.location !== 'all') {
      filteredStations = filteredStations.filter(station =>
        station.location === filters.location
      );
    }

    return filteredStations;
  }, [filters]);

  const locations = React.useMemo(() =>
    Array.from(new Set(mockRefillStations.map(station => station.location))),
    []
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Refill Stations</h1>
          <p className="text-sm text-muted-foreground">
            Manage refill stations and monitor their status
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Station
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search stations..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.location}
                onValueChange={(value) => setFilters(prev => ({ ...prev, location: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map(location => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={stations}
            emptyMessage="No stations found"
            loadingMessage="Loading stations..."
          />
        </CardContent>
      </Card>

      {/* Special Meatball Station Notice */}
      <Card className="bg-[#FFDB00] border-[#0058AB] border-2">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Utensils className="w-12 h-12 text-[#0058AB]" />
              <div>
                <h2 className="text-xl font-bold text-[#0058AB]">HUVUDROLL Meatball Stations</h2>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-[#0058AB]" />
                    <div>
                      <p className="text-[#0058AB]">Temperature Controlled</p>
                      <p className="text-sm text-[#0058AB]">Maintained at 60°C</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-[#0058AB]" />
                    <div>
                      <p className="text-[#0058AB]">Special Equipment</p>
                      <p className="text-sm text-[#0058AB]">Food-grade dispensing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Button className="bg-[#0058AB] hover:bg-[#004F9B]">
              <Plus className="w-4 h-4 mr-2" />
              Add Meatball Station
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 
